import StickyTableResponsive from '../../common/StickyTableResponsive';
import SortableTh from '../../common/SortableTh';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTableSort } from '../../../hooks/useTableSort';
import {
  AdminActionButton,
  AdminDeactivateModal,
  AdminDeleteModal,
  AdminPeriodFields,
  AdminValidityActions,
  AdminValidityBadge,
  ADMIN_WEEKDAYS,
  defaultAdminPeriod,
} from './KkpAdminShared';
import AdminFormLabel from '../../admin/AdminFormLabel';
import SearchableSelect from '../../SearchableSelect';
import KkpPlanningMagInlineCell from '../KkpPlanningMagInlineCell';
import { ADMIN_FIELD_HELP } from '../../../utils/adminHelpContent';
import {
  buildProduitRefLabelMap,
  buildProduitRefOptions,
} from '../../../utils/adminProduitRefOptions';
import clientAnnuaireService from '../../../services/clientAnnuaireService';
import kkpService from '../../../services/kkpService';
import { notify } from '../../common/AppToast';
import { AppButton, AppInput, AppModal, AppPillSwitch, AppSelect, AppSpinner } from '../../ui';

const NL_EMPLACEMENT_SORT = {
  emplacement: { type: 'string', getValue: (row) => row.value },
  type: { type: 'string', getValue: (row) => row.type_news },
  productRef: { type: 'string', getValue: (row) => row.product_ref },
  periode: { type: 'string', getValue: (row) => row.validity_label },
  statut: { type: 'string', getValue: (row) => row.validity_status },
};

const NL_TYPE_SORT = {
  type: { type: 'string', getValue: (row) => row.value },
  prefixe: { type: 'string', getValue: (row) => row.prefix },
  jour: { type: 'string', getValue: (row) => row.weekday_label },
  periode: { type: 'string', getValue: (row) => row.validity_label },
  statut: { type: 'string', getValue: (row) => row.validity_status },
};

const NL_DISPO_SORT = {
  type: { type: 'string', getValue: (row) => row.type_news },
  regle: { type: 'string', getValue: (row) => row.rule_label || row.max_envois_mois },
  periode: { type: 'string', getValue: (row) => row.validity_label },
};

const emptyNlTypeForm = () => ({
  value: '', prefix: '', weekday: 5,
  valid_from_year: new Date().getFullYear(),
  valid_from_month: new Date().getMonth() + 1,
});

const emptyNlDispoForm = () => ({
  type_news: 'FOOD',
  max_envois_mois: 1,
  valid_from_year: new Date().getFullYear(),
  valid_from_month: new Date().getMonth() + 1,
});

const emptyNlEmpForm = (types = []) => ({
  type_news: types[0]?.value || 'SORTIES',
  slot_label: '',
  product_ref: '',
  valid_from_year: new Date().getFullYear(),
  valid_from_month: new Date().getMonth() + 1,
});

const KkpAdminNewsletterSection = () => {
  const [data, setData] = useState({ newsletter: {}, mois: [] });
  const [catalogueProduits, setCatalogueProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [nlTab, setNlTab] = useState('emplacements');
  const [nlDispoForm, setNlDispoForm] = useState(emptyNlDispoForm);
  const [nlTypeForm, setNlTypeForm] = useState(emptyNlTypeForm);
  const [nlEmpForm, setNlEmpForm] = useState(() => emptyNlEmpForm());
  const [empModalOpen, setEmpModalOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [dispoModalOpen, setDispoModalOpen] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivatePeriod, setDeactivatePeriod] = useState(defaultAdminPeriod);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteContextKey, setDeleteContextKey] = useState(null);

  const moisOptions = data.mois ?? [];
  const types = data.newsletter?.types ?? [];
  const emplacements = data.newsletter?.emplacements ?? [];
  const { sortedRows: sortedNlEmp, sort: sortNlEmp, toggleSort: toggleNlEmp, getSortIcon: getNlEmpIcon } = useTableSort(emplacements, NL_EMPLACEMENT_SORT);
  const { sortedRows: sortedNlTypes, sort: sortNlTypes, toggleSort: toggleNlTypes, getSortIcon: getNlTypesIcon } = useTableSort(types, NL_TYPE_SORT);
  const { sortedRows: sortedNlDispo, sort: sortNlDispo, toggleSort: toggleNlDispo, getSortIcon: getNlDispoIcon } = useTableSort(data.newsletter?.dispo_rules ?? [], NL_DISPO_SORT);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [result, catalogue] = await Promise.all([
        kkpService.listPlanningAdmin(),
        clientAnnuaireService.listProduits(),
      ]);
      setData({ newsletter: result.newsletter ?? {}, mois: result.mois ?? [] });
      setCatalogueProduits(catalogue);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const produitRefOptions = useMemo(
    () => buildProduitRefOptions(
      catalogueProduits,
      [
        nlEmpForm.product_ref,
        ...emplacements.map((item) => item.product_ref),
      ],
    ),
    [catalogueProduits, emplacements, nlEmpForm.product_ref],
  );
  const produitRefLabelByValue = useMemo(
    () => buildProduitRefLabelMap(produitRefOptions),
    [produitRefOptions],
  );

  const wrapSave = async (fn, successMessage) => {
    setSaving(true);
    try {
      await fn();
      if (successMessage) notify.success(successMessage);
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const clearInlineEdit = () => {
    setEditingCell(null);
    setEditDraft(null);
  };

  const openEmpModal = () => {
    setNlEmpForm(emptyNlEmpForm(types));
    setEmpModalOpen(true);
  };

  const closeEmpModal = () => {
    setEmpModalOpen(false);
    setNlEmpForm(emptyNlEmpForm(types));
  };

  const submitEmpCreate = () => wrapSave(
    () => kkpService.createPlanningNewsletterEmplacement({
      type_news: nlEmpForm.type_news,
      slot_label: nlEmpForm.slot_label,
      product_ref: nlEmpForm.product_ref || null,
      valid_from_year: nlEmpForm.valid_from_year,
      valid_from_month: nlEmpForm.valid_from_month,
    }).then(() => closeEmpModal()),
    'Emplacement ajouté',
  );

  const startInlineEdit = (id, field) => {
    const row = emplacements.find((item) => item.id === id);
    if (!row) return;
    setEditingCell({ venteId: id, field });
    setEditDraft(field === 'product_ref' ? String(row.product_ref || '').toUpperCase() : (row[field] ?? ''));
  };

  const saveInlineEdit = async (draftOverride) => {
    if (!editingCell || inlineSaving) return;
    const { venteId, field } = editingCell;
    const row = emplacements.find((item) => item.id === venteId);
    if (!row || field !== 'product_ref') {
      clearInlineEdit();
      return;
    }

    const draft = draftOverride !== undefined ? draftOverride : editDraft;
    const nextRef = String(draft ?? '').trim().toUpperCase() || null;
    const prevRef = row.product_ref ? String(row.product_ref).toUpperCase() : null;
    if (nextRef === prevRef) {
      clearInlineEdit();
      return;
    }

    setInlineSaving(true);
    try {
      await kkpService.updatePlanningOption(venteId, { product_ref: nextRef });
      setData((prev) => ({
        ...prev,
        newsletter: {
          ...prev.newsletter,
          emplacements: (prev.newsletter?.emplacements ?? []).map((item) => (
            item.id === venteId ? { ...item, product_ref: nextRef } : item
          )),
        },
      }));
      clearInlineEdit();
      notify.success('Réf. produit enregistrée');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setInlineSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setSaving(true);
    try {
      await kkpService.deactivatePlanningOption(deactivateTarget.id, deactivatePeriod);
      setDeactivateTarget(null);
      notify.success('Option désactivée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async (id) => {
    setSaving(true);
    try {
      await kkpService.reactivatePlanningOption(id);
      notify.success('Option réactivée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await kkpService.deletePlanningOption(deleteTarget.id);
      setDeleteTarget(null);
      setDeleteContextKey(null);
      notify.success('Élément supprimé');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AppSpinner />;

  return (
    <>
      <AppPillSwitch
        className="mb-3"
        ariaLabel="Onglets newsletters"
        value={nlTab}
        onChange={(value) => {
          setNlTab(value);
          clearInlineEdit();
          setEmpModalOpen(false);
          setTypeModalOpen(false);
          setDispoModalOpen(false);
        }}
        options={[
          { value: 'emplacements', label: 'Emplacements', icon: 'mdi:view-grid-outline' },
          { value: 'types', label: 'Types', icon: 'mdi:email-newsletter' },
          { value: 'dispo', label: 'Disponibilité', icon: 'mdi:calendar-check-outline' },
        ]}
      />

      {nlTab === 'emplacements' && (
        <div className="card admin-data-card">
          <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
            <span>Emplacements ({sortedNlEmp.length})</span>
            <AppButton
              size="sm"
              variant="danger-600"
              icon="mdi:plus"
              disabled={saving || inlineSaving || types.length === 0}
              onClick={openEmpModal}
            >
              Ajouter
            </AppButton>
          </div>
          <StickyTableResponsive>
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <SortableTh sortKey="emplacement" label="Emplacement" sort={sortNlEmp} onSort={toggleNlEmp} getSortIcon={getNlEmpIcon} />
                  <SortableTh sortKey="type" label="Type" sort={sortNlEmp} onSort={toggleNlEmp} getSortIcon={getNlEmpIcon} />
                  <SortableTh sortKey="productRef" label="Réf. produit" sort={sortNlEmp} onSort={toggleNlEmp} getSortIcon={getNlEmpIcon} />
                  <SortableTh sortKey="periode" label="Période" sort={sortNlEmp} onSort={toggleNlEmp} getSortIcon={getNlEmpIcon} />
                  <SortableTh sortKey="statut" label="Statut" sort={sortNlEmp} onSort={toggleNlEmp} getSortIcon={getNlEmpIcon} />
                  <SortableTh label="Actions" className="text-end" sortable={false} />
                </tr>
              </thead>
              <tbody>
                {sortedNlEmp.map((item) => (
                  <tr key={item.id} className={item.validity_status === 'expired' ? 'opacity-50' : ''}>
                    <td className="fw-medium text-sm">{item.value}</td>
                    <td>{item.type_news}</td>
                    <KkpPlanningMagInlineCell
                      venteId={item.id}
                      field="product_ref"
                      type="searchable-select"
                      options={produitRefOptions}
                      allowEmpty
                      emptyLabel="— Aucune —"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className="text-sm"
                    >
                      {produitRefLabelByValue.get(String(item.product_ref || '').toUpperCase()) || item.product_ref || '—'}
                    </KkpPlanningMagInlineCell>
                    <td className="text-sm">{item.validity_label}</td>
                    <td><AdminValidityBadge status={item.validity_status} /></td>
                    <td className="text-end">
                      <AdminValidityActions
                        item={item}
                        saving={saving || inlineSaving}
                        onDeactivate={(t) => { setDeactivateTarget(t); setDeactivatePeriod(defaultAdminPeriod()); }}
                        onReactivate={handleReactivate}
                        onDelete={(t) => {
                          setDeleteTarget(t);
                          setDeleteContextKey('newsletter-emplacement');
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StickyTableResponsive>
        </div>
      )}

      {nlTab === 'types' && (
        <div className="card admin-data-card">
          <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
            <span>Types ({sortedNlTypes.length})</span>
            <AppButton
              size="sm"
              variant="danger-600"
              icon="mdi:plus"
              disabled={saving}
              onClick={() => {
                setNlTypeForm(emptyNlTypeForm());
                setTypeModalOpen(true);
              }}
            >
              Ajouter
            </AppButton>
          </div>
          <StickyTableResponsive>
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <SortableTh sortKey="type" label="Type" sort={sortNlTypes} onSort={toggleNlTypes} getSortIcon={getNlTypesIcon} />
                  <SortableTh sortKey="prefixe" label="Préfixe" sort={sortNlTypes} onSort={toggleNlTypes} getSortIcon={getNlTypesIcon} />
                  <SortableTh sortKey="jour" label="Jour" sort={sortNlTypes} onSort={toggleNlTypes} getSortIcon={getNlTypesIcon} />
                  <SortableTh sortKey="periode" label="Période" sort={sortNlTypes} onSort={toggleNlTypes} getSortIcon={getNlTypesIcon} />
                  <SortableTh sortKey="statut" label="Statut" sort={sortNlTypes} onSort={toggleNlTypes} getSortIcon={getNlTypesIcon} />
                  <SortableTh label="Actions" className="text-end" sortable={false} />
                </tr>
              </thead>
              <tbody>
                {sortedNlTypes.map((item) => (
                  <tr key={item.id}>
                    <td className="fw-medium">{item.value}</td>
                    <td className="text-sm">{item.prefix}</td>
                    <td>{item.weekday_label}</td>
                    <td className="text-sm">{item.validity_label}</td>
                    <td><AdminValidityBadge status={item.validity_status} /></td>
                    <td className="text-end">
                      <AdminValidityActions
                        item={item}
                        saving={saving}
                        onDeactivate={(t) => { setDeactivateTarget(t); setDeactivatePeriod(defaultAdminPeriod()); }}
                        onReactivate={handleReactivate}
                        onDelete={(t) => {
                          setDeleteTarget(t);
                          setDeleteContextKey('newsletter-type');
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StickyTableResponsive>
        </div>
      )}

      {nlTab === 'dispo' && (
        <>
          <p className="text-sm text-secondary-light mb-3">
            Limite le nombre d&apos;envois par mois pour un type (ex. FOOD = 1er mercredi seulement).
            La règle s&apos;applique à partir du mois choisi, sans effet rétroactif sur les mois précédents.
            Les lignes indisponibles sont masquées dans le planning ; les ventes déjà saisies restent visibles.
          </p>
          <div className="card admin-data-card">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
              <span>Règles de disponibilité ({sortedNlDispo.length})</span>
              <AppButton
                size="sm"
                variant="danger-600"
                icon="mdi:plus"
                disabled={saving || types.length === 0}
                onClick={() => {
                  setNlDispoForm({
                    ...emptyNlDispoForm(),
                    type_news: types[0]?.value || 'FOOD',
                  });
                  setDispoModalOpen(true);
                }}
              >
                Ajouter
              </AppButton>
            </div>
            <StickyTableResponsive>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <SortableTh sortKey="type" label="Type" sort={sortNlDispo} onSort={toggleNlDispo} getSortIcon={getNlDispoIcon} />
                    <SortableTh sortKey="regle" label="Règle" sort={sortNlDispo} onSort={toggleNlDispo} getSortIcon={getNlDispoIcon} />
                    <SortableTh sortKey="periode" label="Période" sort={sortNlDispo} onSort={toggleNlDispo} getSortIcon={getNlDispoIcon} />
                    <SortableTh label="Actions" className="text-end" sortable={false} />
                  </tr>
                </thead>
                <tbody>
                  {sortedNlDispo.map((rule) => (
                    <tr key={rule.id}>
                      <td className="fw-medium">{rule.type_news}</td>
                      <td>{rule.max_envois_label}</td>
                      <td className="text-sm">{rule.validity_label}</td>
                      <td className="text-end">
                        <AdminActionButton
                          title="Supprimer"
                          icon="mdi:delete-outline"
                          className="btn-outline-danger"
                          disabled={saving}
                          onClick={() => wrapSave(() => kkpService.deleteNewsletterDispoRule(rule.id), 'Règle supprimée')}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          </div>
        </>
      )}

      <AppModal
        show={empModalOpen}
        title="Ajouter un emplacement"
        onClose={closeEmpModal}
        size="lg"
        centered
        contentAs="form"
        contentProps={{
          onSubmit: (e) => {
            e.preventDefault();
            submitEmpCreate();
          },
        }}
        footer={(
          <>
            <AppButton type="button" variant="outline-secondary" onClick={closeEmpModal} disabled={saving}>
              Annuler
            </AppButton>
            <AppButton type="submit" variant="danger-600" loading={saving}>
              Ajouter
            </AppButton>
          </>
        )}
      >
        <div className="row g-3">
          <div className="col-md-4">
            <AdminFormLabel help="Type de newsletter auquel rattacher cet emplacement." required>Type</AdminFormLabel>
            <AppSelect
              value={nlEmpForm.type_news}
              onChange={(e) => setNlEmpForm((f) => ({ ...f, type_news: e.target.value }))}
            >
              {types.map((t) => <option key={t.id} value={t.value}>{t.value}</option>)}
            </AppSelect>
          </div>
          <div className="col-md-4">
            <AdminFormLabel help={ADMIN_FIELD_HELP.nlSlot.help} helpExample={ADMIN_FIELD_HELP.nlSlot.helpExample} required>
              Emplacement (slot)
            </AdminFormLabel>
            <AppInput
              required
              placeholder="PAVE MIDDLE"
              value={nlEmpForm.slot_label}
              onChange={(e) => setNlEmpForm((f) => ({ ...f, slot_label: e.target.value }))}
            />
          </div>
          <div className="col-md-4">
            <AdminFormLabel help={ADMIN_FIELD_HELP.productRef.help} helpExample={ADMIN_FIELD_HELP.productRef.helpExample}>
              Réf. produit
            </AdminFormLabel>
            <SearchableSelect
              value={nlEmpForm.product_ref}
              onChange={(value) => setNlEmpForm((f) => ({ ...f, product_ref: value }))}
              options={produitRefOptions}
              allowEmpty
              emptyOptionLabel="— Aucune —"
              placeholder="Produit en vente…"
              emptyLabel="Aucun produit catalogue"
              disabled={saving}
            />
          </div>
          <AdminPeriodFields
            year={nlEmpForm.valid_from_year}
            month={nlEmpForm.valid_from_month}
            moisOptions={moisOptions}
            onYearChange={(y) => setNlEmpForm((f) => ({ ...f, valid_from_year: y }))}
            onMonthChange={(m) => setNlEmpForm((f) => ({ ...f, valid_from_month: m }))}
          />
        </div>
      </AppModal>

      <AppModal
        show={typeModalOpen}
        title="Ajouter un type de newsletter"
        onClose={() => setTypeModalOpen(false)}
        size="lg"
        centered
        contentAs="form"
        contentProps={{
          onSubmit: (e) => {
            e.preventDefault();
            wrapSave(
              () => kkpService.createPlanningNewsletterType(nlTypeForm).then(() => {
                setTypeModalOpen(false);
                setNlTypeForm(emptyNlTypeForm());
              }),
              'Type ajouté',
            );
          },
        }}
        footer={(
          <>
            <AppButton type="button" variant="outline-secondary" onClick={() => setTypeModalOpen(false)} disabled={saving}>
              Annuler
            </AppButton>
            <AppButton type="submit" variant="danger-600" loading={saving}>
              Ajouter
            </AppButton>
          </>
        )}
      >
        <div className="row g-3">
          <div className="col-md-4">
            <AdminFormLabel help={ADMIN_FIELD_HELP.nlTypeCode.help} helpExample={ADMIN_FIELD_HELP.nlTypeCode.helpExample} required>
              Code
            </AdminFormLabel>
            <AppInput required value={nlTypeForm.value} onChange={(e) => setNlTypeForm((f) => ({ ...f, value: e.target.value }))} />
          </div>
          <div className="col-md-4">
            <AdminFormLabel help={ADMIN_FIELD_HELP.nlPrefix.help} helpExample={ADMIN_FIELD_HELP.nlPrefix.helpExample}>
              Préfixe
            </AdminFormLabel>
            <AppInput placeholder="NEWS FOOD" value={nlTypeForm.prefix} onChange={(e) => setNlTypeForm((f) => ({ ...f, prefix: e.target.value }))} />
          </div>
          <div className="col-md-4">
            <AdminFormLabel help={ADMIN_FIELD_HELP.nlWeekday.help} helpExample={ADMIN_FIELD_HELP.nlWeekday.helpExample}>
              Jour
            </AdminFormLabel>
            <AppSelect value={nlTypeForm.weekday} onChange={(e) => setNlTypeForm((f) => ({ ...f, weekday: Number(e.target.value) }))}>
              {ADMIN_WEEKDAYS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </AppSelect>
          </div>
          <AdminPeriodFields
            year={nlTypeForm.valid_from_year}
            month={nlTypeForm.valid_from_month}
            moisOptions={moisOptions}
            onYearChange={(y) => setNlTypeForm((f) => ({ ...f, valid_from_year: y }))}
            onMonthChange={(m) => setNlTypeForm((f) => ({ ...f, valid_from_month: m }))}
          />
        </div>
      </AppModal>

      <AppModal
        show={dispoModalOpen}
        title="Ajouter une règle de disponibilité"
        onClose={() => setDispoModalOpen(false)}
        size="lg"
        centered
        contentAs="form"
        contentProps={{
          onSubmit: (e) => {
            e.preventDefault();
            wrapSave(
              () => kkpService.createNewsletterDispoRule(nlDispoForm).then(() => {
                setDispoModalOpen(false);
                setNlDispoForm(emptyNlDispoForm());
              }),
              'Règle ajoutée',
            );
          },
        }}
        footer={(
          <>
            <AppButton type="button" variant="outline-secondary" onClick={() => setDispoModalOpen(false)} disabled={saving}>
              Annuler
            </AppButton>
            <AppButton type="submit" variant="danger-600" loading={saving}>
              Ajouter
            </AppButton>
          </>
        )}
      >
        <div className="row g-3">
          <div className="col-md-4">
            <AdminFormLabel required>Type</AdminFormLabel>
            <AppSelect value={nlDispoForm.type_news} onChange={(e) => setNlDispoForm((f) => ({ ...f, type_news: e.target.value }))}>
              {types.map((t) => (
                <option key={t.id} value={t.value}>{t.value}</option>
              ))}
            </AppSelect>
          </div>
          <div className="col-md-4">
            <AdminFormLabel help={ADMIN_FIELD_HELP.nlDispoMax.help} helpExample={ADMIN_FIELD_HELP.nlDispoMax.helpExample}>
              Envois max / mois
            </AdminFormLabel>
            <AppSelect value={nlDispoForm.max_envois_mois} onChange={(e) => setNlDispoForm((f) => ({ ...f, max_envois_mois: Number(e.target.value) }))}>
              <option value={0}>Tous les mercredis / vendredis</option>
              <option value={1}>1er uniquement</option>
              <option value={2}>2 premiers</option>
              <option value={3}>3 premiers</option>
              <option value={4}>4 premiers</option>
            </AppSelect>
          </div>
          <AdminPeriodFields
            year={nlDispoForm.valid_from_year}
            month={nlDispoForm.valid_from_month}
            moisOptions={moisOptions}
            onYearChange={(y) => setNlDispoForm((f) => ({ ...f, valid_from_year: y }))}
            onMonthChange={(m) => setNlDispoForm((f) => ({ ...f, valid_from_month: m }))}
          />
        </div>
      </AppModal>

      <AdminDeactivateModal
        target={deactivateTarget}
        period={deactivatePeriod}
        moisOptions={moisOptions}
        saving={saving}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
        onPeriodChange={setDeactivatePeriod}
      />
      <AdminDeleteModal
        target={deleteTarget}
        contextKey={deleteContextKey}
        saving={saving}
        onClose={() => { setDeleteTarget(null); setDeleteContextKey(null); }}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default KkpAdminNewsletterSection;
